import { ActionIcon, Avatar, Button, Divider, Group, Input, Menu, Modal, Paper, Stack, Text, TextInput } from "@mantine/core";
import { getAvatarOfDid, useATProtoAuthStore } from "../../../../lib/atproto/useATProtoStore";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { IconArrowRight, IconCheck, IconDots, IconExternalLink, IconPlus, IconX } from "@tabler/icons-react";
import { useAtProtoHandleQuery } from "../../../../lib/atproto/useAtProtoHandleQuery";
import type { Did } from "@atcute/lexicons";

export const ATProtoSettings = () => {
	return (
		<Stack>
			<ATProtoAccountsList />
			<ATProtoAddAccount />
		</Stack>
	);
}

export const ATProtoAccountsList = () => {
	const sessions = useATProtoAuthStore(store => store.sessions);

	return (
		<Stack w="100%" gap={4}>
			{sessions?.map(did => (
				<ATProtoAccountItem key={did} did={did} />
			))}
		</Stack>
	);
};

export const ATProtoAccountItem = ({ did }: { did: Did }) => {
	const agent = useATProtoAuthStore(store => store.agent);
	const signIn = useATProtoAuthStore(store => store.signIn);
	const signOut = useATProtoAuthStore(store => store.signOut);
	const handle = useAtProtoHandleQuery(did as Did<"plc" | "web">);
	const isCurrent = agent?.sub === did;

	return (
		<Paper w="100%" withBorder p={4}>
			<Group gap={4} wrap="nowrap" align="center">
				<ActionIcon
					size="input-xs"
					color="gray"
					variant="subtle"
				>
					<Avatar
						src={getAvatarOfDid(did)}
						size="sm"
					/>
				</ActionIcon>
				<Button
					variant="subtle"
					color="gray"
					size="xs"
					flex="1"
					justify="space-between"
					styles={{ root: { padding: 4 }, label: { overflow: "visible" } }}
					rightSection={isCurrent && (
						<IconCheck size={16} color="green" style={{ marginInlineEnd: 0 }} />
					)}
					onClick={() => signIn(did)}
				>
					<Text span inline c={isCurrent ? "unset" : "dimmed"}>
						{handle.data ?? did}
					</Text>
				</Button>
				<Menu>
					<Menu.Target>
						<ActionIcon
							size="input-xs"
							color="gray"
							variant="subtle"
						>
							<IconDots size={16} />
						</ActionIcon>
					</Menu.Target>
					<Menu.Dropdown>
						<Menu.Item
							component="a"
							href={`https://pds.ls/at://${did}`}
							target="_blank"
							leftSection={<IconExternalLink size={16} />}
						>
							View on pds.ls
						</Menu.Item>
						<Menu.Item
							component="a"
							href={`https://bsky.app/profile/${did}`}
							target="_blank"
							leftSection={<IconExternalLink size={16} />}
						>
							View on bsky.app
						</Menu.Item>
						<Menu.Item
							color="red"
							onClick={() => signOut(did)}
							leftSection={<IconX size={16} />}
						>
							Sign Out
						</Menu.Item>
					</Menu.Dropdown>
				</Menu>
			</Group>
		</Paper>
	);
}

export const ATProtoAddAccount = () => {
	const [opened, { open, close }] = useDisclosure(false);
	const [identifier, setIdentifier] = useState("");
	const [loading, setLoading] = useState(false);

	const onSubmit = async () => {
		setLoading(true);
		await useATProtoAuthStore.getState().startAuthorization(identifier);
		// unreachable code after redirect
	};

	const onAtpassport = () => {
		setLoading(true);
		const callbackUrl = window.location.origin + "/atpassport/callback";
		window.location.href = "https://atpassport.net/authentication?" + new URLSearchParams({
			callback: callbackUrl,
		}).toString();
	};

	return (
		<Stack gap={4}>
			<Button
				leftSection={<IconPlus size={16} />}
				onClick={open}
				color="gray"
			>
				Add Account
			</Button>

			<Modal
				opened={opened}
				onClose={close}
				title="Sign in"
				centered
			>
				<Stack gap={4}>
					<TextInput
						label="Internet Handle"
						placeholder="example.bsky.social"
						name="handle"
						value={identifier}
						onChange={e => setIdentifier(e.currentTarget.value)}
						onSubmit={onSubmit}
						onKeyDown={e => {
							if (e.key == "Enter") onSubmit();
						}}
						autoFocus
					/>

					<Button
						disabled={!identifier}
						loading={loading}
						onClick={onSubmit}
						rightSection={<IconArrowRight />}
						fullWidth
					>
						Continue
					</Button>

					<Divider label="Or" my="md" />

					<Button
						loading={loading}
						onClick={onAtpassport}
						leftSection={<img src="https://atpassport.net/favicon.ico" width={24} height={24} />}
					>
						Sign in with @passport
					</Button>
				</Stack>
			</Modal>
		</Stack >
	);
};
